import React, { useState, useEffect } from 'react';
import { Modal, Select, Card, Typography, Space, Empty, App, Tag } from 'antd';
import { SwapOutlined, EnvironmentOutlined, TableOutlined } from '@ant-design/icons';
import { Table } from '../../../types/tables';
import { useGetAreasQuery, useGetTablesByAreaQuery, useTransferTableMutation } from '../../../services/tablesApi';

const { Title, Text } = Typography;
const { Option } = Select;

interface TransferTableModalProps {
  visible: boolean;
  sourceTable?: Table;
  onCancel: () => void;
  onSuccess: () => void;
}

const TransferTableModal: React.FC<TransferTableModalProps> = ({
  visible,
  sourceTable,
  onCancel,
  onSuccess,
}) => {
  const { message } = App.useApp();
  const [selectedAreaId, setSelectedAreaId] = useState<string | undefined>(undefined);
  const [selectedTableId, setSelectedTableId] = useState<string | undefined>(undefined);

  // API queries
  const { data: areas = [] } = useGetAreasQuery();
  const { data: tables = [], isLoading: tablesLoading } = useGetTablesByAreaQuery(
    selectedAreaId!,
    { skip: !selectedAreaId }
  );

  // Mutation
  const [transferTable, { isLoading: transferLoading }] = useTransferTableMutation();

  // Pre-select the source table's area when modal opens
  useEffect(() => {
    if (visible && sourceTable?.areaId) {
      setSelectedAreaId(sourceTable.areaId);
    }
  }, [visible, sourceTable?.areaId]);

  // Reset selection when modal closes
  useEffect(() => {
    if (!visible) {
      setSelectedTableId(undefined);
    }
  }, [visible]);

  // Filter to only show available tables (excluding the source table)
  const availableTables = tables.filter(
    table => table.status === 'Available' && table.id !== sourceTable?.id
  );

  const handleTransfer = async () => {
    if (!sourceTable?.id || !selectedTableId) {
      message.error('Debe seleccionar una mesa destino');
      return;
    }

    try {
      await transferTable({
        fromTableId: sourceTable.id,
        toTableId: selectedTableId,
      }).unwrap();

      message.success('Mesa transferida exitosamente');
      onSuccess();
    } catch (error: any) {
      console.error('Error al transferir mesa:', error);
      message.error(error?.data?.message || 'Error al transferir la mesa');
    }
  };

  const handleCancel = () => {
    setSelectedTableId(undefined);
    onCancel();
  };

  const selectedDestinationTable = availableTables.find(t => t.id === selectedTableId);

  return (
    <Modal
      title={null}
      open={visible}
      onCancel={handleCancel}
      onOk={handleTransfer}
      confirmLoading={transferLoading}
      width={500}
      okText="Transferir"
      cancelText="Cancelar"
      style={{ top: 50 }}
      zIndex={1100}
      styles={{ body: { padding: 0 } }}
      okButtonProps={{
        disabled: !selectedTableId,
        size: 'large',
        icon: <SwapOutlined />,
      }}
      cancelButtonProps={{ size: 'large' }}
    >
      <div style={{ padding: '20px' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          <Title level={3} style={{ marginBottom: '4px', color: '#1890ff' }}>
            <SwapOutlined style={{ marginRight: '8px' }} />
            Transferir Mesa
          </Title>
          <Text type="secondary">
            Mover la orden a otra mesa disponible
          </Text>
        </div>

        {/* Source Table Info */}
        <Card
          size="small"
          style={{ marginBottom: '16px', backgroundColor: 'var(--bg-error-light)', borderColor: 'var(--border-error)' }}
          styles={{ body: { padding: '12px' } }}
        >
          <Space>
            <TableOutlined style={{ color: '#f5222d', fontSize: '18px' }} />
            <div>
              <Text strong>Mesa Origen: </Text>
              <Tag color="red">{sourceTable?.number}</Tag>
              <Text type="secondary" style={{ marginLeft: '8px' }}>
                {sourceTable?.areaName}
              </Text>
            </div>
          </Space>
        </Card>

        {/* Area Selection */}
        <div style={{ marginBottom: '16px' }}>
          <Text strong style={{ display: 'block', marginBottom: '8px' }}>
            <EnvironmentOutlined style={{ marginRight: '8px' }} />
            Seleccionar Área
          </Text>
          <Select
            value={selectedAreaId}
            onChange={(value) => {
              setSelectedAreaId(value);
              setSelectedTableId(undefined); // Reset table selection when area changes
            }}
            style={{ width: '100%' }}
            size="large"
            placeholder="Seleccione un área"
          >
            {areas.map(area => (
              <Option key={area.id} value={area.id}>
                {area.name}
              </Option>
            ))}
          </Select>
        </div>

        {/* Destination Table Selection */}
        <div style={{ marginBottom: '16px' }}>
          <Text strong style={{ display: 'block', marginBottom: '8px' }}>
            <TableOutlined style={{ marginRight: '8px' }} />
            Mesa Destino
          </Text>
          {selectedAreaId ? (
            tablesLoading ? (
              <Card size="small" loading />
            ) : availableTables.length > 0 ? (
              <Select
                value={selectedTableId}
                onChange={setSelectedTableId}
                style={{ width: '100%' }}
                size="large"
                placeholder="Seleccione la mesa destino"
                showSearch
                optionFilterProp="children"
              >
                {availableTables.map(table => (
                  <Option key={table.id} value={table.id}>
                    Mesa {table.number} - Capacidad: {table.capacity}
                  </Option>
                ))}
              </Select>
            ) : (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No hay mesas disponibles en esta área"
                style={{ margin: '20px 0' }}
              />
            )
          ) : (
            <Card
              size="small"
              style={{ backgroundColor: 'var(--bg-secondary)', textAlign: 'center' }}
              styles={{ body: { padding: '20px' } }}
            >
              <Text type="secondary">Seleccione un área primero</Text>
            </Card>
          )}
        </div>

        {/* Destination Table Info (when selected) */}
        {selectedDestinationTable && (
          <Card
            size="small"
            style={{ backgroundColor: 'var(--bg-success-light)', borderColor: 'var(--border-success)' }}
            styles={{ body: { padding: '12px' } }}
          >
            <Space>
              <TableOutlined style={{ color: '#52c41a', fontSize: '18px' }} />
              <div>
                <Text strong>Mesa Destino: </Text>
                <Tag color="green">{selectedDestinationTable.number}</Tag>
                <Text type="secondary" style={{ marginLeft: '8px' }}>
                  Capacidad: {selectedDestinationTable.capacity} personas
                </Text>
              </div>
            </Space>
          </Card>
        )}

        {/* Info Note */}
        <div style={{ marginTop: '16px', padding: '12px', backgroundColor: 'var(--bg-info-light)', borderRadius: '8px' }}>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            <strong>Nota:</strong> Al transferir, la mesa origen quedará disponible y todos los productos
            de la orden se moverán a la nueva mesa.
          </Text>
        </div>
      </div>
    </Modal>
  );
};

export default TransferTableModal;
